import {useCallback, useEffect, useState} from 'react';
import { APIBaseUrl } from '../../i18n/environments';
import useCurrentLanguage from '../../i18n/useCurrentLanguage';
import { setObjectInLocalStorage } from '../../utils/storage';
import { getSearchParam } from '../../utils/url';

export interface Promo {
  id: number;
  add_type: number;
  app_type: number;
  exclude: Array<string>;
  first_time_transaction: boolean;
  guest: boolean;
  include: Array<string>;
  placement_type: number;
  promo_code: string;
  sales_pitch: string;
  show_countdown: boolean;
  start_date: string;
  sub_heading: string;
  time: null | number;
}

interface PromoCookie {
  cache_expiration: number;
  promoCode: string;
  promos: Array<Promo>;
}

interface Props {
  showBanner?: any;
  setShowBanner?: (prop: Promo | boolean) => void;
  promoCookie?: PromoCookie;
  setPromoCookie?: (cookie: PromoCookie) => void;
  isMobile?: boolean;
}

const HALF_HOUR_MILISECONDS = 1000 * 60 * 30;

const setPromoCookieInStorage = (promoCookie: PromoCookie) => {
  if (!promoCookie) return;
  setObjectInLocalStorage('promoCookie', promoCookie);
};

const usePromotionBanners = ({ showBanner, setShowBanner, promoCookie, setPromoCookie, isMobile }: Props) => {
  const { isEs } = useCurrentLanguage();

  // Request banners from BE
  const getPromoBanners = useCallback(async (promoCode: string) => {
    let oktaId = undefined;
    const localTime = new Date().getTime();
    const oktaUserInfo = localStorage.getItem('okta-user-info');
    if (oktaUserInfo) {
      oktaId = JSON.parse(oktaUserInfo).sub;
    }

    const params = `${promoCode ? `&promo_code=${promoCode}` : ''}${oktaId ? `&okta_id=${oktaId}` : ''}`;

    try {
      const response = await fetch(`${APIBaseUrl}/promotions/anonymous?lang=${isEs ? 'es' : 'en'}${params}`);
      const promos = await response.json();
      const storageObj = {
        ...(promoCode && { promoCode: promoCode }),
        promos: promos,
        cache_expiration: localTime + HALF_HOUR_MILISECONDS,
      };
      setPromoCookie(storageObj);
      setPromoCookieInStorage(storageObj);
    } catch (err) {
      console.log('Promo banners: ', err);
    }
  }, []);

  // If promotion banner active, increase "main" container margin-top to compensate header height
  const setBannerHeight = useCallback(() => {
    const header = typeof window !== 'undefined' && window && document.getElementById('header');
    const body = typeof document.body !== 'undefined' && window && document.body;
    const promotions =
      header.querySelectorAll('.promotions').length > 0 ? (header.querySelectorAll('.promotions')[0] as HTMLElement).offsetHeight : 0;
    body.style.paddingTop = `${promotions}px`;
    if (isMobile && promotions > 0) {
      body.style.paddingTop = `${promotions}px`;
    }

    const closeBanner = header.querySelectorAll('.close__panel').length > 0 ? header.querySelectorAll('.close__panel')[0] : undefined;
    if (closeBanner !== undefined) {
      closeBanner.addEventListener('click', () => {
        body.style.paddingTop = '0';
      });
    }
  }, [isMobile]);

  // Dismiss banner on button click
  const dismissBanner = (id: string | number) => {
    const dismissPromo: any = localStorage.getItem('dismissed_promo');
    const dismissedIds = dismissPromo ? JSON.parse(dismissPromo) : [];
    if (!dismissedIds.includes(id)) {
      dismissedIds.push(id);
    }
    localStorage.setItem('dismissed_promo', JSON.stringify(dismissedIds));
    setShowBanner(false);
  };

  const getLocalStorageData = () => {
    const hasPurchased = localStorage.getItem('has_purchased');
    const pathName = window.location.pathname;
    let parseDismissed = [];
    let dismissPromo: any = localStorage.getItem('dismissed_promo');
    let userInfo: any = localStorage.getItem('okta-user-info');

    if (userInfo) {
      userInfo = JSON.parse(userInfo);
    } else {
      userInfo = undefined;
    }

    if (dismissPromo) {
      parseDismissed = JSON.parse(dismissPromo) ?? [];
      dismissPromo = parseDismissed.map((i: string) => Number(i));
    } else {
      dismissPromo = undefined;
    }

    return { pathName, userInfo, hasPurchased, dismissPromo };
  };

  const productSpecific = () => {
    const { pathName, userInfo, hasPurchased, dismissPromo } = getLocalStorageData();
    const productSpecific = promoCookie?.promos?.filter(
      (promo) =>
        (promo?.include.includes(pathName) || (promo?.exclude?.length !== 0 && !promo.exclude?.includes(pathName))) &&
        (!dismissPromo || !dismissPromo.includes(promo.id))
    );

    const firstTimeBuyer = productSpecific?.filter((b) => b.first_time_transaction && !hasPurchased);
    const newCustomer = productSpecific?.filter((b) => b.guest && !userInfo);

    return newCustomer?.length ? newCustomer : firstTimeBuyer?.length ? firstTimeBuyer : productSpecific;
  };

  const defaultOpt = () => {
    const { userInfo, hasPurchased, dismissPromo } = getLocalStorageData();
    return promoCookie?.promos?.filter(
      (promo) => (!dismissPromo || !dismissPromo.includes(promo.id)) && promo.include.length === 0 && promo.exclude.length === 0
    );
  };

  const setBanner = () => {
    const productBanner = productSpecific();
    const defaultBanner = defaultOpt();

    setShowBanner(productBanner?.length ? productBanner[0] : defaultBanner?.length ? defaultBanner[0] : false);
  };

  useEffect(() => {
    let promoCode = getSearchParam('zbcode') ? getSearchParam('zbcode') : null;
    const userAgent = navigator.userAgent;

    if (
      (!promoCookie ||
      promoCookie?.cache_expiration < new Date().getDate() + HALF_HOUR_MILISECONDS ||
      (promoCode && promoCookie?.promoCode && promoCookie?.promoCode !== promoCode)) &&
      !/Googlebot|Google|Lighthouse|Chrome-Lighthouse|MOTO/i.test(userAgent)
    ) {
      getPromoBanners(promoCode);
    }
    setBanner();
  }, [promoCookie]);

  useEffect(() => {
    setBannerHeight();
  }, [showBanner]);

  return {
    showBanner,
    promoCookie,
    dismissBanner,
    setBannerHeight,
  };
};

export default usePromotionBanners;
